<template>
  <section
    v-if="featureEnabled"
    ref="rootEl"
    class="jcl"
    data-testid="job-categories-list"
  >
    <header class="jcl-header">
      <h2 class="jcl-header__title">{{ headerTitle }}</h2>
      <p v-if="headerSubtitle" class="jcl-header__subtitle">{{ headerSubtitle }}</p>
    </header>

    <div v-if="!isAccountEmpty" class="jcl-toolbar">
      <SearchBar
        data-testid="job-categories-search"
        class="jcl-toolbar__search"
        :label="moreFiltersLabel"
        ghost-text="Search category by name"
        :length="appliedFilterChips.length"
        :add="true"
        :filter-btn="true"
        :btn-text="'Add Job Category'"
        action-button-icon="plusicon.svg"
        :chip-text="appliedFilterChips"
        :value-search="filters.search || ''"
        :counter-section="false"
        @onSearch="onSearch"
        @onClick="onToolbarClick"
        @removeFilter="onRemoveFilter"
      />
    </div>

    <div class="jcl-body">
      <!-- Loading skeleton for the table body -->
      <div v-if="loading" class="jcl-loading" role="status" aria-live="polite">
        <span class="jcl-visually-hidden">Loading job categories</span>
        <div v-for="n in 3" :key="n" class="jcl-skeleton-row" aria-hidden="true">
          <span class="jcl-skeleton-bar jcl-skeleton-bar--wide" />
          <span class="jcl-skeleton-bar" />
          <span class="jcl-skeleton-bar jcl-skeleton-bar--short" />
          <span class="jcl-skeleton-bar jcl-skeleton-bar--short" />
          <span class="jcl-skeleton-bar jcl-skeleton-bar--short" />
          <span class="jcl-skeleton-bar jcl-skeleton-bar--icon" />
          <span class="jcl-skeleton-bar jcl-skeleton-bar--icon" />
        </div>
      </div>

      <table v-else-if="rows.length" class="jcl-table" :aria-busy="loading">
        <caption class="jcl-visually-hidden">
          Job categories
        </caption>
        <colgroup>
          <col class="jcl-col-category" />
          <col class="jcl-col-score" />
          <col class="jcl-col-total-score" />
          <col class="jcl-col-positions" />
          <col class="jcl-col-status" />
          <col class="jcl-col-icon" />
          <col class="jcl-col-icon" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="jcl-th">Job Categories</th>
            <th scope="col" class="jcl-th">4-Factor Score</th>
            <th scope="col" class="jcl-th">Total Score</th>
            <th scope="col" class="jcl-th"># Positions</th>
            <th scope="col" class="jcl-th">Status</th>
            <th scope="col" class="jcl-th jcl-th--center">
              <span class="jcl-visually-hidden">Chatter</span>
            </th>
            <th scope="col" class="jcl-th jcl-th--center">
              <span class="jcl-visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.id"
            class="jcl-row"
            :class="rowClasses(row, index)"
          >
            <td class="jcl-cell jcl-cell--category">
              <button
                type="button"
                class="jcl-name-btn"
                :aria-label="`Open ${row.name}`"
                data-testid="category-name"
                @click="onRowClick(row)"
              >
                <OrgObjectCard
                  :name="row.name"
                  :codeValue="row.code"
                  :code="Boolean(row.code)"
                  :icon="false"
                  :type="'category'"
                  :state="row.cardState"
                  :allow_hover="false"
                  :showDisabledHover="false"
                  :showTooltip="false"
                  data-testid="category-card"
                />
              </button>
            </td>

            <td class="jcl-cell jcl-cell--score">
              <div class="jcl-gauges" role="group" :aria-label="row.gaugeSummary">
                <div v-for="gauge in row.gauges" :key="gauge.key" class="jcl-gauge">
                  <div class="jcl-gauge__heading">
                    <EXTooltip
                      :title="gauge.fullLabel"
                      :description="gauge.definition"
                      placement="top"
                      :delay="200"
                    >
                      <span class="jcl-gauge__label" :data-testid="`gauge-label-${gauge.key}`">{{
                        gauge.label
                      }}</span>
                    </EXTooltip>
                    <span class="jcl-gauge__value">{{ gauge.display }}</span>
                  </div>
                  <span class="jcl-gauge__track">
                    <span
                      class="jcl-gauge__fill"
                      :class="{ 'jcl-gauge__fill--muted': row.disabled }"
                      :style="{ width: gauge.pct + '%' }"
                    />
                  </span>
                </div>
              </div>
            </td>

            <td class="jcl-cell jcl-cell--total-score">
              <span class="jcl-total-score">
                <span class="jcl-total-score__value">{{ row.totalScoreDisplay }}</span>
                <span v-if="row.totalScore !== null" class="jcl-total-score__max">
                  /{{ row.totalScoreMax }}
                </span>
              </span>
            </td>

            <td class="jcl-cell jcl-cell--positions">
              <button
                type="button"
                class="jcl-positions-btn"
                :disabled="row.disabled || row.positionCount === 0"
                :aria-label="`View ${row.positionCount} positions for ${row.name}`"
                data-testid="positions-count-button"
                @click="onPositionsClick(row)"
              >
                <AttachedToPositionsCounterButton
                  :count="row.positionCount"
                  :disabled="row.disabled"
                  data-testid="positions-count"
                />
              </button>
            </td>

            <td class="jcl-cell jcl-cell--status">
              <div class="jcl-status">
                <OrgObjectStatusBadge
                  :status="row.badgeStatus"
                  :neutral="row.badgeNeutral"
                  data-testid="category-status-badge"
                />
                <EXTooltip
                  v-if="row.needsReview"
                  title="Needs Review"
                  description="This category uses a retired job-evaluation framework. Review and rescore to clear this flag."
                  placement="top"
                  :delay="200"
                >
                  <button
                    type="button"
                    class="jcl-review-btn"
                    aria-label="Needs Review"
                    data-testid="needs-review-icon"
                    @click="onNeedsReview(row)"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="9" cy="9" r="7.5" stroke="#DC6803" stroke-width="1.5" />
                      <path
                        d="M9 6v3.2M9 12.2h.01"
                        stroke="#DC6803"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                </EXTooltip>
              </div>
            </td>

            <td class="jcl-cell jcl-cell--icon">
              <button
                type="button"
                class="jcl-icon-btn"
                :aria-label="`Open chatter for ${row.name}`"
                data-testid="row-chatter"
                @click="onRowChatter(row)"
              >
                <ChatterIcon :unread="row.unread" />
              </button>
            </td>

            <td class="jcl-cell jcl-cell--icon" @click.stop>
              <JobCategoryLineMenu
                :category="row"
                :status="row.status"
                :archived="row.archived"
                :position-count="row.positionCount"
                @action="onLineMenuAction(row, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <StandarPageWithListNoData
        v-else-if="isAccountEmpty"
        :stack-margin-top="32"
        class="jcl-empty-account"
        data-testid="job-categories-empty-account"
      >
        <template #content>
          <div class="jcl-empty-account__content" role="status">
            <p class="jcl-empty-account__title">No job categories yet</p>
            <p class="jcl-empty-account__description">
              Group positions of equal value by adding a job category.
            </p>
            <Button
              :general="true"
              plusIcon="add-icon.svg"
              label="Add your first job category"
              data-testid="job-categories-empty-add"
              @onClick="emit('add-category')"
            />
          </div>
        </template>
      </StandarPageWithListNoData>

      <div v-else class="jcl-empty" role="status">
        <p class="jcl-empty__title">No job categories found</p>
        <p class="jcl-empty__subtitle">Try changing the filters or clearing your search.</p>
      </div>
    </div>

    <ExPagination
      v-if="pagination.pageCount > 1"
      class="jcl-pagination"
      :pagination="paginationForEx"
      @onRequest="onPaginationRequest"
    />
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import SearchBar from "@components/SearchBar/SearchBar.vue";
import Button from "@components/buttons/Button.vue";
import StandarPageWithListNoData from "@components/common/StandarPageWithListNoData.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import AttachedToPositionsCounterButton from "@components/AttachedToPositionsCounterButton/AttachedToPositionsCounterButton.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import JobCategoryLineMenu from "@components/JobCategoryLineMenu/JobCategoryLineMenu.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";
import { buildRowView } from "./jobCategoriesListRow.js";

/**
 * ER-11346 — JobCategoriesList (Organization > Job Categories table view).
 *
 * Presentational composition of existing shared components (Filters bar/SearchBar,
 * OrgObjectCard type="category", OrgObjectStatusBadge,
 * AttachedToPositionsCounterButton, ChatterIcon, ExPagination)
 * for the Organization > Job Categories screen. The parent owns data fetching,
 * filtering/pagination state and the filter modal; this component re-emits
 * intent through `filter-change`, `more-filters`, `add-category`, `row-chatter`,
 * `row-action`, `row-click`, `positions-click`, `needs-review` and `page-change`.
 *
 * Shipped behind the linked idea flag (idea-MDP-566, "Job Category — Equal-Value
 * Grouping for Fair Pay (EU Directive Alignment)"). Kill-switch semantics: renders
 * by default; set the flag to false to hide the new table until a screen wires it.
 */
const props = defineProps({
  categories: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    default: () => ({ status: "", search: "" }),
  },
  pagination: {
    type: Object,
    default: () => ({ page: 1, pageCount: 1 }),
  },
  appliedFilterChips: {
    type: Array,
    default: () => [],
  },
  headerTitle: {
    type: String,
    default: "Job Categories",
  },
  headerSubtitle: {
    type: String,
    default: "Flat groupings used to compare positions of equal value",
  },
  isAccountEmpty: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "add-category",
  "filter-change",
  "more-filters",
  "remove-filter",
  "row-chatter",
  "row-actions",
  "row-action",
  "row-click",
  "positions-click",
  "needs-review",
  "page-change",
]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);
const rootEl = ref(null);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const rows = computed(() => props.categories.map((category) => buildRowView(category)));

const moreFiltersLabel = computed(() =>
  props.appliedFilterChips.length > 0 ? "More filters" : "Filters"
);

const paginationForEx = computed(() => {
  const pageCount = Number(props.pagination.pageCount) || 1;
  const requestedPage = Number(props.pagination.page) || 1;
  return {
    page: Math.min(Math.max(requestedPage, 1), pageCount),
    total: pageCount,
    perPage: 1,
  };
});

function rowClasses(row, index) {
  return {
    "jcl-row--alt": index % 2 === 1,
    "jcl-row--disabled": row.disabled,
  };
}

function onSearch(searchText) {
  emit("filter-change", { ...props.filters, search: searchText });
}

function onToolbarClick(payload) {
  if (payload === "add") {
    emit("add-category");
    return;
  }
  emit("more-filters");
}

function onRemoveFilter(chipLabel) {
  emit("remove-filter", chipLabel);
}

function onRowChatter(row) {
  emit("row-chatter", row.id);
}

function onRowClick(row) {
  emit("row-click", row.id);
}

function onPositionsClick(row) {
  if (row.disabled || row.positionCount === 0) return;
  emit("positions-click", row.id);
}

function onNeedsReview(row) {
  emit("needs-review", row.id);
}

function onLineMenuAction(row, payload) {
  const action = payload && payload.type;
  if (!action) return;
  emit("row-action", { id: row.id, action });
  emit("row-actions", row.id);
}

function onPaginationRequest({ page }) {
  emit("page-change", page);
}
</script>

<style scoped>
.jcl {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.jcl-header {
  padding: 24px 24px 4px;
}

.jcl-header__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.jcl-header__subtitle {
  margin: 4px 0 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jcl-toolbar {
  display: flex;
  align-items: center;
  padding: 16px 24px 20px;
}

.jcl-toolbar__search {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.jcl-body {
  width: 100%;
  overflow-x: auto;
}

.jcl-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.jcl-col-category {
  width: 32%;
}

.jcl-col-score {
  width: 26%;
}

.jcl-col-total-score {
  width: 10%;
}

.jcl-col-positions {
  width: 10%;
}

.jcl-col-status {
  width: 12%;
}

.jcl-col-icon {
  width: 5%;
}

.jcl-table thead th {
  padding: 12px 16px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.jcl-table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.jcl-table tbody tr:last-child td {
  border-bottom: none;
}

.jcl-row--alt td {
  background: #fcfcfd;
}

.jcl-th--center {
  text-align: center;
}

.jcl-cell--icon {
  text-align: center;
}

.jcl-cell--category,
.jcl-cell--total-score,
.jcl-cell--positions,
.jcl-cell--status {
  text-align: left;
}

.jcl-name-btn,
.jcl-positions-btn,
.jcl-review-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.jcl-name-btn {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
}

.jcl-positions-btn:disabled {
  cursor: default;
}

.jcl-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.jcl-gauges {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  min-width: 260px;
}

.jcl-gauge {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.jcl-gauge__heading {
  display: flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
}

.jcl-gauge__label {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #475467;
  text-align: left;
}

.jcl-gauge__track {
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background: #eaecf0;
  overflow: hidden;
}

.jcl-gauge__fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: #7f56d9;
}

.jcl-gauge__value {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
  font-variant-numeric: tabular-nums;
}

.jcl-row--disabled .jcl-gauge__label,
.jcl-row--disabled .jcl-gauge__value {
  color: #98a2b3;
}

.jcl-row--disabled .jcl-gauge__track {
  background: #f2f4f7;
}

.jcl-row--disabled .jcl-gauge__fill {
  background: #98a2b3;
}

.jcl-total-score {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-family: Inter, sans-serif;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.jcl-total-score__value {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #6941c6;
}

.jcl-total-score__max {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667185;
}

.jcl-row--disabled .jcl-total-score__value {
  color: #98a2b3;
}

.jcl-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #475467;
  cursor: pointer;
}

.jcl-icon-btn:hover {
  background: #f2edff;
  color: #7f56d9;
}

.jcl-icon-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.jcl-loading {
  padding: 8px 24px;
}

.jcl-skeleton-row {
  display: grid;
  grid-template-columns: 2.2fr 1.6fr 0.5fr 0.6fr 0.6fr 0.4fr 0.4fr;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #eaecf0;
}

.jcl-skeleton-row:last-child {
  border-bottom: none;
}

.jcl-skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 9999px;
  background: #f2f4f7;
}

.jcl-skeleton-bar--wide {
  width: 70%;
}

.jcl-skeleton-bar--short {
  width: 45%;
}

.jcl-skeleton-bar--icon {
  width: 24px;
  height: 24px;
  justify-self: center;
}

.jcl-empty {
  padding: 40px 24px;
  text-align: center;
}

.jcl-empty__title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jcl-empty__subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.jcl-empty-account__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 32px;
  text-align: center;
}

.jcl-empty-account__title {
  margin: 0 0 8px;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jcl-empty-account__description {
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jcl-pagination {
  border-top: 1px solid #eaecf0;
}

.jcl-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
